"use client";

import React from "react";
import { Loader as Loader2, Send, CircleCheck as CheckCircle2, CircleAlert as AlertCircle } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="mb-6 rounded-lg bg-green-500/20 p-3">
          <CheckCircle2 className="size-12 text-green-500" />
        </div>
        <h3 className="mb-2 text-2xl font-bold font-default text-foreground">Messaggio Inviato!</h3>
        <p className="mb-8 text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="min-w-[200px]">
          Invia un altro messaggio
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Il nome è obbligatorio",
        minLength: {
          value: 2,
          message: "Inserisci almeno 2 caratteri"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Nome</FormLabel>
              <FormControl>
                <Input placeholder="Il tuo nome" {...field} className="bg-input/50 border-border focus-visible:ring-primary transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="surname" rules={{
        required: "Il cognome è obbligatorio"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Cognome</FormLabel>
              <FormControl>
                <Input placeholder="Il tuo cognome" {...field} className="bg-input/50 border-border focus-visible:ring-primary transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" rules={{
        required: "L'email è obbligatoria",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Inserisci un indirizzo email valido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="tua@email.com" {...field} className="bg-input/50 border-border focus-visible:ring-primary transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Il numero di telefono è obbligatorio",
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Inserisci un numero di telefono valido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Telefono</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+39 123 456 7890" {...field} className="bg-input/50 border-border focus-visible:ring-primary transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="company" rules={{
      required: "Il nome dell'azienda è obbligatorio"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground">Azienda</FormLabel>
            <FormControl>
              <Input placeholder="Nome della tua azienda" {...field} className="bg-input/50 border-border focus-visible:ring-primary transition-colors" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Il messaggio è obbligatorio",
      minLength: {
        value: 10,
        message: "Il messaggio deve contenere almeno 10 caratteri"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground">Messaggio</FormLabel>
            <FormControl>
              <Textarea placeholder="Scrivi qui il tuo messaggio..." className="min-h-[150px] resize-y bg-input/50 border-border focus-visible:ring-primary transition-colors" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="flex items-center gap-3 rounded-lg bg-destructive/10 p-4 text-destructive border border-destructive/20">
          <AlertCircle className="size-5 shrink-0" />
          <p className="text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full py-6 text-base font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:shadow-xl hover:shadow-primary/30">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-5 animate-spin" />
            Invio in corso...
          </> : <>
            Invia Messaggio
            <Send className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}
export default function ContactForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="contactform" className="bg-background py-24 px-6 md:py-32 overflow-hidden relative min-h-screen">
      <div className="mx-auto max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20 items-center">
          
          <div ref={ref} className={cn("lg:col-span-2 flex flex-col justify-center transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12")}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-primary/10 text-primary text-sm font-medium mb-6 w-fit">
              <span className="relative flex size-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-lg size-2 bg-primary"></span>
              </span>
              Disponibili per nuovi progetti
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default text-foreground mb-6 leading-tight">
              Lavoriamo <br className="hidden lg:block" />
              <span className="text-primary">Insieme.</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Hai un'idea in mente o vuoi semplicemente dire ciao? Compila il modulo e il nostro team ti risponderà il prima possibile per discutere i dettagli.
            </p>
          </div>

          <div className={cn("lg:col-span-3 transition-all duration-1000 delay-200 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
            <div className="dark bg-card border border-border rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden">
              <div className="absolute -top-24 -right-24 size-64 bg-primary/10 rounded-lg blur-3xl pointer-events-none"></div>
              <div className="absolute -bottom-24 -left-24 size-64 bg-primary/5 rounded-lg blur-3xl pointer-events-none"></div>
              
              <div className="relative z-10">
                <FormProvider formId="contact-form-main" sectionName="ContactForm" defaultValues={{
                name: "",
                surname: "",
                email: "",
                phone: "",
                company: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                surname: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                company: {
                  required: true
                },
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Invia Messaggio" successMessage="Grazie per averci contattato! Ti risponderemo al più presto." errorMessage="Si è verificato un errore durante l'invio. Riprova più tardi.">
                  <ContactFormInner />
                </FormProvider>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}